import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Plus, Edit, Trash2, Loader2, Wallet } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
} from '@/components/ui/pagination';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatDateOnly = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const typeBadge = (type) => {
  const colors = {
    expense: 'bg-red-100 text-red-700',
    income: 'bg-green-100 text-green-700',
  };
  return colors[type] || 'bg-gray-100 text-gray-700';
};

const categoryTypeBadge = (type) => {
  const colors = {
    expense: 'bg-red-100 text-red-700',
    income: 'bg-green-100 text-green-700',
    both: 'bg-[#C97A5E]/10 text-[#C97A5E]'
  };
  return colors[type] || 'bg-gray-100 text-gray-700';
};

export default function ExpensesPage() {
  const [activeTab, setActiveTab] = useState('entries');

  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', category_type: 'both', is_active: true, ledger_account_id: '' });
  const [categorySaving, setCategorySaving] = useState(false);

  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(true);
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entryForm, setEntryForm] = useState({
    entry_type: 'expense',
    category_id: '',
    amount: '',
    entry_date: '',
    description: '',
    payment_type: 'cash',
    expense_scope: 'company'
  });

  const [filters, setFilters] = useState({
    search: '',
    entry_type: 'all',
    category_id: 'all',
    date_from: '',
    date_to: ''
  });

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchCategories();
  }, [categorySearch]);

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    fetchEntries();
  }, [filters, page]);

  const activeCategories = useMemo(() => categories.filter((c) => c.is_active), [categories]);

  const fetchCategories = async () => {
    try {
      const response = await adminApi.getExpenseCategories({ search: categorySearch || undefined });
      setCategories(response.data || []);
    } catch (error) {
      toast.error('Failed to load categories');
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await adminApi.getAccounts();
      setAccounts(response.data || []);
    } catch (error) {
      toast.error('Failed to load ledger accounts');
    }
  };

  const fetchEntries = async () => {
    setEntriesLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
      };
      if (filters.search) params.search = filters.search;
      if (filters.entry_type !== 'all') params.entry_type = filters.entry_type;
      if (filters.category_id !== 'all') params.category_id = filters.category_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      const response = await adminApi.getExpenses(params);
      setEntries(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load entries');
    } finally {
      setEntriesLoading(false);
    }
  };

  const openCategoryModal = (category) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        category_type: category.category_type || 'both',
        is_active: category.is_active,
        ledger_account_id: category.ledger_account_id || '',
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', category_type: 'both', is_active: true, ledger_account_id: '' });
    }
    setCategoryModalOpen(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Please enter category name');
      return;
    }
    setCategorySaving(true);
    try {
      if (editingCategory) {
        await adminApi.updateExpenseCategory(editingCategory.id, categoryForm);
        toast.success('Category updated');
      } else {
        await adminApi.createExpenseCategory(categoryForm);
        toast.success('Category created');
      }
      setCategoryModalOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save category');
    } finally {
      setCategorySaving(false);
    }
  };

  const deleteCategory = async (categoryId) => {
    const confirm = window.confirm('Delete this category?');
    if (!confirm) return;
    try {
      await adminApi.deleteExpenseCategory(categoryId);
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const openEntryModal = (entry) => {
    if (entry) {
      setEditingEntry(entry);
      setEntryForm({
        entry_type: entry.entry_type,
        category_id: entry.category_id,
        amount: String(entry.amount ?? ''),
        entry_date: entry.entry_date || '',
        description: entry.description || '',
        payment_type: entry.payment_type || 'cash',
        expense_scope: entry.expense_scope || 'company'
      });
    } else {
      setEditingEntry(null);
      setEntryForm({
        entry_type: 'expense',
        category_id: '',
        amount: '',
        entry_date: '',
        description: '',
        payment_type: 'cash',
        expense_scope: 'company'
      });
    }
    setEntryModalOpen(true);
  };

  const saveEntry = async () => {
    if (!entryForm.category_id) {
      toast.error('Please select a category');
      return;
    }
    if (!entryForm.amount) {
      toast.error('Please enter amount');
      return;
    }
    if (!entryForm.entry_date) {
      toast.error('Please select date');
      return;
    }

    const payload = {
      entry_type: entryForm.entry_type,
      category_id: entryForm.category_id,
      amount: Number(entryForm.amount),
      entry_date: entryForm.entry_date,
      description: entryForm.description,
      payment_type: entryForm.payment_type,
      expense_scope: entryForm.expense_scope
    };

    try {
      if (editingEntry) {
        await adminApi.updateExpense(editingEntry.id, payload);
        toast.success('Entry updated');
      } else {
        await adminApi.createExpense(payload);
        toast.success('Entry created');
      }
      setEntryModalOpen(false);
      fetchEntries();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save entry');
    }
  };

  const deleteEntry = async (entryId) => {
    const confirm = window.confirm('Delete this entry?');
    if (!confirm) return;
    try {
      await adminApi.deleteExpense(entryId);
      toast.success('Entry deleted');
      fetchEntries();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete entry');
    }
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Expenses</h1>
        <p className="text-[#A8A8A8] mt-1">Track miscellaneous expenses and income</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="entries">Entries</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
        </TabsList>

        <TabsContent value="entries" className="space-y-4">
          <Card className="bg-white border-[#E5E5E5]">
            <CardContent className="p-4">
              <div className="flex flex-col lg:flex-row gap-4">
                <Input
                  placeholder="Search description..."
                  value={filters.search}
                  onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
                <Select value={filters.entry_type} onValueChange={(v) => setFilters((prev) => ({ ...prev, entry_type: v }))}>
                  <SelectTrigger className="w-full lg:w-48 border-[#E5E5E5]">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.category_id} onValueChange={(v) => setFilters((prev) => ({ ...prev, category_id: v }))}>
                  <SelectTrigger className="w-full lg:w-60 border-[#E5E5E5]">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters((prev) => ({ ...prev, date_from: e.target.value }))}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters((prev) => ({ ...prev, date_to: e.target.value }))}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
                <Button
                  className="bg-[#C97A5E] hover:bg-[#B06850]"
                  onClick={() => openEntryModal(null)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Entry
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E5E5E5]">
            <CardContent className="p-0">
              {entriesLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
                </div>
              ) : entries.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-[#A8A8A8]">
                  <Wallet className="w-12 h-12 mb-4" />
                  <p>No entries found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase">Type</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase">Category</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase">Date</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase">Amount</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase">Payment</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase">Scope</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase">Description</th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {entries.map((entry) => (
                        <tr key={entry.id} className="hover:bg-[#FAFAF9]/50">
                          <td className="py-4 px-6">
                            <Badge className={cn('text-xs', typeBadge(entry.entry_type))}>{entry.entry_type}</Badge>
                          </td>
                          <td className="py-4 px-6 text-[#2D2420]">{entry.category_name}</td>
                          <td className="py-4 px-6 text-[#A8A8A8]">{formatDateOnly(entry.entry_date)}</td>
                          <td className="py-4 px-6 font-semibold text-[#2D2420]">{formatCurrency(entry.amount)}</td>
                          <td className="py-4 px-6 text-[#2D2420] capitalize">{entry.payment_type || 'cash'}</td>
                          <td className="py-4 px-6 text-[#2D2420] capitalize">{entry.expense_scope || 'company'}</td>
                          <td className="py-4 px-6">
                            <Badge className={cn('text-xs', entry.status === 'POSTED' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700')}>
                              {entry.status || 'DRAFT'}
                            </Badge>
                          </td>
                          <td className="py-4 px-6 text-[#A8A8A8] max-w-xs truncate">{entry.description || '-'}</td>
                          <td className="py-4 px-6 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEntryModal(entry)}
                              className="text-[#5C4033]"
                              disabled={entry.status === 'POSTED'}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteEntry(entry.id)}
                              className="text-red-600"
                              disabled={entry.status === 'POSTED'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
            {!entriesLoading && total > 0 && (
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4 border-t border-[#E5E5E5]">
                <p className="text-sm text-[#A8A8A8]">
                  Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total}
                </p>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={(e) => {
                          e.preventDefault();
                          setPage((prev) => Math.max(1, prev - 1));
                        }}
                        className={page === 1 ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                    <PaginationItem>
                      <span className="px-3 text-sm text-[#5C4033]">
                        Page {page} of {Math.max(1, Math.ceil(total / pageSize))}
                      </span>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext
                        onClick={(e) => {
                          e.preventDefault();
                          setPage((prev) => Math.min(Math.max(1, Math.ceil(total / pageSize)), prev + 1));
                        }}
                        className={page >= Math.max(1, Math.ceil(total / pageSize)) ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="space-y-4">
          <Card className="bg-white border-[#E5E5E5]">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <Input
                  placeholder="Search category..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
                <Button
                  className="bg-[#C97A5E] hover:bg-[#B06850]"
                  onClick={() => openCategoryModal(null)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Category
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E5E5E5]">
            <CardContent className="p-0">
              {categories.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-[#A8A8A8]">
                  <Wallet className="w-10 h-10 mb-3" />
                  <p>No categories found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase">Type</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {categories.map((category) => (
                        <tr key={category.id} className="hover:bg-[#FAFAF9]/50">
                          <td className="py-4 px-6 text-[#2D2420] font-medium">{category.name}</td>
                          <td className="py-4 px-6">
                            <Badge className={cn('text-xs', categoryTypeBadge(category.category_type))}>{category.category_type}</Badge>
                          </td>
                          <td className="py-4 px-6">
                            <Badge className={cn('text-xs', category.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600')}>
                              {category.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="py-4 px-6 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openCategoryModal(category)}
                              className="text-[#5C4033]"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteCategory(category.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={categoryModalOpen} onOpenChange={setCategoryModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Name</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm((prev) => ({ ...prev, name: e.target.value }))}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Category Type</Label>
              <Select
                value={categoryForm.category_type}
                onValueChange={(v) => setCategoryForm((prev) => ({ ...prev, category_type: v }))}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Both</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                  <SelectItem value="income">Income</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Ledger Account</Label>
              <Select
                value={categoryForm.ledger_account_id || 'none'}
                onValueChange={(v) => setCategoryForm((prev) => ({ ...prev, ledger_account_id: v === 'none' ? '' : v }))}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select ledger account" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No mapping</SelectItem>
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.name}{account.code ? ` (${account.code})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Status</Label>
              <Select
                value={categoryForm.is_active ? 'active' : 'inactive'}
                onValueChange={(v) => setCategoryForm((prev) => ({ ...prev, is_active: v === 'active' }))}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveCategory}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
              disabled={categorySaving}
            >
              {categorySaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={entryModalOpen} onOpenChange={setEntryModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">{editingEntry ? 'Edit Entry' : 'Add Entry'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Type</Label>
              <Select value={entryForm.entry_type} onValueChange={(v) => setEntryForm((prev) => ({ ...prev, entry_type: v }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">Expense</SelectItem>
                  <SelectItem value="income">Income</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Category</Label>
              <Select value={entryForm.category_id} onValueChange={(v) => setEntryForm((prev) => ({ ...prev, category_id: v }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {activeCategories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Amount</Label>
              <Input
                type="number"
                value={entryForm.amount}
                onChange={(e) => setEntryForm((prev) => ({ ...prev, amount: e.target.value }))}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Date</Label>
              <Input
                type="date"
                value={entryForm.entry_date}
                onChange={(e) => setEntryForm((prev) => ({ ...prev, entry_date: e.target.value }))}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Description</Label>
              <Textarea
                value={entryForm.description}
                onChange={(e) => setEntryForm((prev) => ({ ...prev, description: e.target.value }))}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#5C4033]">Payment Type</Label>
                <Select value={entryForm.payment_type} onValueChange={(v) => setEntryForm((prev) => ({ ...prev, payment_type: v }))}>
                  <SelectTrigger className="border-[#E5E5E5]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#5C4033]">Expense Scope</Label>
                <Select value={entryForm.expense_scope} onValueChange={(v) => setEntryForm((prev) => ({ ...prev, expense_scope: v }))}>
                  <SelectTrigger className="border-[#E5E5E5]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="personal">Personal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {entryForm.expense_scope === 'company' && !activeCategories.find((category) => category.id === entryForm.category_id)?.ledger_account_id ? (
              <p className="text-sm text-amber-700">
                Company entries require a category with a mapped ledger account.
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEntryModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveEntry}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
